import React, { useEffect, useState } from "react";

import { Box, Grid, IconButton, Modal } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useAppState } from "../../../../context/StateContext";
import { Contact } from "../../../../models/contact";
import { Timezone } from "../../../../models/timezone";
import { formatTimezone } from "../../../../util/Format";
import { phoneInputSettings, phoneNumberIsValid } from "../../../../util/formatPhoneNumber";
import AutoSuggest from "../../../Form/AutoSuggest";
import Button, { Color } from "../../../Form/Button";
import Checkbox from "../../../Form/Checkbox";
import Input from "../../../Form/Input";
import PhoneInput from "../../../Form/PhoneInput";
import useStyles from "./styles";

interface Props {
  contact?: Contact;
  handleClose?: (contact?: Contact) => void;
  open: boolean;
  timezones?: Timezone[];
}

const ContactModal = (props: Props) => {
  const { currentUser } = useAppState();

  const { open, handleClose, contact, timezones } = props;

  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [mobile, setMobile] = useState<string>();
  const [phone, setPhone] = useState<string | null>();
  const [timezone, setTimezone] = useState<Timezone>();
  const [timezoneId, setTimezoneId] = useState<number>();

  const [includeCommunications, setIncludeCommunications] = useState<boolean>(false);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const classes = useStyles();

  const { t: translate } = useTranslation();

  useEffect(() => {
    if (isSubmitted) handleValidate();
  }, [isSubmitted, firstName, lastName, email, mobile, timezoneId]);

  useEffect(() => {
    if (contact) {
      setFirstName(contact?.firstName);
      setLastName(contact?.lastName);
      setEmail(contact?.email);
      setMobile(contact?.mobilePhone);
      setPhone(contact?.phone);
      setTimezone(timezones?.find((timezone) => timezone.id === contact?.timezoneId));
      setTimezoneId(contact?.timezoneId);
    }
  }, [contact]);

  useEffect(() => {
    if (open === false) {
      setFirstName(undefined);
      setLastName(undefined);
      setEmail(undefined);
      setMobile(undefined);
      setPhone(undefined);
      setTimezone(undefined);
      setTimezoneId(undefined);
      setIncludeCommunications(false);
      setIsSubmitted(false);
      setErrors([]);
    }
  }, [open]);

  const handleValidate = () => {
    try {
      const schema = yup.object().shape({
        firstName: yup.string().nullable().required(translate("youMustEnterAFirstName")),
        lastName: yup.string().nullable().required(translate("youMustEnterALastName")),
        email: yup.string().nullable().required(translate("youMustEnterAEmail")),
        mobile: yup.string().nullable().required(translate("youMustEnterAMobile")),
        timezoneId: yup.number().nullable().required(translate("youMustEnterATimezone")).nullable(),
      });

      schema.validateSync(
        {
          firstName,
          lastName,
          email,
          mobile,
          timezoneId,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    if (handleValidate()) {
      const updatedContact = {
        firstName,
        lastName,
        email,
        mobilePhone: mobile,
        phone,
        companyId: currentUser?.companyId,
        timezoneId: timezone?.id,
        timezone,
        valid: true,
      } as Contact;

      if (handleClose) {
        if (updatedContact && updatedContact?.id) {
          handleClose({ ...contact, ...updatedContact });
        } else {
          handleClose(updatedContact);
        }
      }
    }
  };

  const fieldHasError = (field: string) => !!errors?.find((error) => error.path === field);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  return (
    <Modal
      open={open}
      onClose={() => handleClose && handleClose()}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div>
        <div className={classes.paper}>
          <div className={classes.modalBody}>
            <Grid container>
              <Grid item xs={12} className={classes.header}>
                <div className={classes.locationModalTitle}>
                  <Box>
                    <span data-cy="locationModalTitle" className={classes.titleName}>
                      {contact ? translate("editAContact") : translate("addNewContact")}
                    </span>
                  </Box>
                  <Box>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => handleClose && handleClose()}
                      size="large">
                      <CloseIcon data-cy="locationCloseIcon" fontSize="medium" />
                    </IconButton>
                  </Box>
                </div>

                <Box>
                  <span className={classes.subTitle}>
                    {contact
                      ? translate("editABusinessContact")
                      : translate("createANewBusinessContact")}
                  </span>
                </Box>
              </Grid>
              <Grid container direction="row" className={classes.gridContainerContact} spacing={2}>
                <Grid item xs={12}>
                  <span className={classes.info}>{translate("contactDetails")}</span>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    label={translate("firstName")}
                    onChange={(event) => setFirstName(event.target.value)}
                    required
                    value={firstName}
                    error={fieldHasError("firstName")}
                    errorMessage={getErrorByField("firstName")}
                    data-cy="contact-first-name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    label={translate("lastName")}
                    onChange={(event) => setLastName(event.target.value)}
                    required
                    value={lastName}
                    error={fieldHasError("lastName")}
                    errorMessage={getErrorByField("lastName")}
                    data-cy="contact-last-name"
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Input
                    label={translate("email")}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    value={email}
                    error={fieldHasError("email")}
                    errorMessage={getErrorByField("email")}
                    data-cy="contact-email"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    label={translate("mobile")}
                    onChange={(value) => {
                      if (phoneNumberIsValid(value)) {
                        setMobile(value);
                      }
                    }}
                    {...phoneInputSettings(currentUser?.company)}
                    required
                    value={mobile}
                    error={fieldHasError("mobile")}
                    errorMessage={getErrorByField("mobile")}
                    data-cy="contact-mobile"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    label={translate("phone")}
                    {...phoneInputSettings(currentUser?.company)}
                    onChange={(value) => {
                      if (phoneNumberIsValid(value)) {
                        setPhone(value || null);
                      }
                    }}
                    value={phone}
                    data-cy="contact-phone"
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <AutoSuggest
                    label={translate("timezone")}
                    placeholder={translate("searchTimezone")}
                    data-cy="timezone"
                    options={timezones}
                    inputValue={timezone ? formatTimezone(timezone) : ""}
                    value={timezones ? timezone : undefined}
                    onChange={(timezone?: Timezone) => {
                      setTimezone(timezone);
                      setTimezoneId(timezone?.id);
                    }}
                    required
                    error={fieldHasError("timezoneId")}
                    errorMessage={getErrorByField("timezoneId")}
                    getOptionLabel={(timezone: Timezone) => formatTimezone(timezone)}
                    getOptionSelected={(option: Timezone, value: Timezone) =>
                      option?.id === value?.id
                    }
                  />
                </Grid>
                {/* //TODO: Attribute to include in communications */}
                {false && (
                  <Grid item xs={12}>
                    <Checkbox
                      checked={includeCommunications}
                      onChange={(event, checked) => setIncludeCommunications(checked)}
                      label={translate("includeInCommunications")}
                    />
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12} className={classes.locationButtonsContainer}>
                <Button
                  customColor={Color.CANCEL}
                  data-cy="contact-cancel-button"
                  onClick={() => handleClose && handleClose()}
                >
                  {translate("cancel")}
                </Button>
                <Button
                  customColor={Color.SUCCESS}
                  data-cy="contact-save-button"
                  onClick={() => handleSubmit()}
                  className={classes.buttonSave}
                >
                  {contact ? translate("update") : translate("addContact")}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ContactModal;
